import React from 'react';
import { Link } from 'react-router-dom';
import toursData from '../data/tours.json';
import './TripsGrid.css';

function TripsGrid({ searchQuery, selectedCategory, selectedDuration, priceRange }) {
  // Convert tours data to array and filter
  const allTours = Object.values(toursData.tours).map(tour => {
    const category = tour.category ? tour.category.trim() : 'Silver';
    return {
      id: tour.id,
      name: tour.name ? tour.name.trim() : 'Unnamed Tour',
      description: tour.description || '',
      duration: tour.duration || 'N/A',
      price: parseInt(String(tour.price).replace(/,/g, '')) || 0,
      rating: tour.rating || '4.5',
      image: tour.image || '/assets/imgs/destination/serengeti1.jpg',
      category: category || 'Silver',
      highlights: tour.highlights || []
    };
  });

  // Filter tours
  const filteredTours = allTours.filter((tour) => {
    // Search filter
    const matchesSearch = searchQuery === '' || 
      tour.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      tour.description.toLowerCase().includes(searchQuery.toLowerCase());

    // Category filter
    const matchesCategory = selectedCategory === 'all' || 
      tour.category === selectedCategory ||
      (selectedCategory === 'Silver' && (!tour.category || tour.category.trim() === ''));

    // Duration filter
    const matchesDuration = (() => {
      if (selectedDuration === 'all') return true;
      const days = parseInt(tour.duration) || 0;
      if (selectedDuration === '1-3') return days >= 1 && days <= 3;
      if (selectedDuration === '4-6') return days >= 4 && days <= 6;
      if (selectedDuration === '7-9') return days >= 7 && days <= 9;
      if (selectedDuration === '10+') return days >= 10;
      return true;
    })();

    // Price filter
    const matchesPrice = (() => {
      if (priceRange === 'all') return true;
      if (priceRange === 'budget') return tour.price < 1000;
      if (priceRange === 'mid') return tour.price >= 1000 && tour.price <= 2500;
      if (priceRange === 'premium') return tour.price > 2500 && tour.price <= 4000;
      if (priceRange === 'luxury') return tour.price > 4000;
      return true;
    })();

    return matchesSearch && matchesCategory && matchesDuration && matchesPrice;
  });

  const getCategoryColor = (category) => {
    switch(category) {
      case 'Luxury': return '#D4AF37';
      case 'Mid-Range': return '#445441';
      case 'Silver': return '#6B7280';
      case 'Budget': return '#10B981';
      default: return '#445441';
    }
  };

  return (
    <div className="trips-grid-container">
      <div className="trips-grid-header">
        <h2 className="trips-grid-title">
          {filteredTours.length} {filteredTours.length === 1 ? 'Tour' : 'Tours'} Available
        </h2>
        <p className="trips-grid-subtitle">
          Browse our carefully curated selection of Tanzania adventures
        </p>
      </div>

      {filteredTours.length > 0 ? (
        <div className="trips-grid">
          {filteredTours.map((tour) => (
            <div key={tour.id} className="trip-card-full">
              <Link 
                to={`/trips/${tour.id}`}
                className="trip-card-link"
              >
                <div className="trip-card-image-wrapper">
                  <img 
                    src={tour.image} 
                    alt={tour.name}
                    className="trip-card-image"
                    onError={(e) => {
                      e.target.src = '/assets/imgs/destination/serengeti1.jpg';
                    }}
                  />
                  <div className="trip-card-overlay">
                    <span 
                      className="trip-card-category"
                      style={{ backgroundColor: getCategoryColor(tour.category) }}
                    >
                      {tour.category}
                    </span>
                    {tour.rating && (
                      <div className="trip-card-rating">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="#FFD700" stroke="#FFD700">
                          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                        </svg>
                        <span>{tour.rating}</span>
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="trip-card-content">
                  <h3 className="trip-card-name">{tour.name}</h3>
                  <p className="trip-card-description">{tour.description}</p>
                  
                  <div className="trip-card-info">
                    <div className="trip-info-item">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10"/>
                        <polyline points="12 6 12 12 16 14"/>
                      </svg>
                      <span>{tour.duration}</span>
                    </div>
                    <div className="trip-info-item">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
                      </svg>
                      <span>From ${tour.price.toLocaleString()}</span>
                    </div>
                  </div>

                  {tour.highlights && tour.highlights.length > 0 && (
                    <div className="trip-card-highlights">
                      <ul>
                        {tour.highlights.slice(0, 3).map((highlight, index) => (
                          <li key={index}>{highlight}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className="trip-card-footer">
                    <span className="trip-card-cta">
                      View Details
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M5 12h14M12 5l7 7-7 7"/>
                      </svg>
                    </span>
                  </div>
                </div>
              </Link>
            </div>
          ))}
        </div>
      ) : (
        <div className="trips-empty">
          <p>No tours found matching your criteria.</p>
          <p className="trips-empty-suggestion">Try adjusting your filters or search terms.</p>
        </div>
      )}
    </div>
  );
}

export default TripsGrid;

